if (!customElements.get('skn-home-lineup')) {
  customElements.define('skn-home-lineup', class extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-offer-card]')];
      this.offers = [...this.querySelectorAll('[data-offer]')];
      this.bar = this.querySelector('.skn-shop-offer');
      this.grid = this.querySelector('.skn-shop-all__grid');
      this.dialog = this.querySelector('dialog');
      if (!this.offers.length || !this.bar) return;
      this.controller = new AbortController();
      const options = { passive: true, signal: this.controller.signal };
      this.active = 0;
      this.distance = 0;
      this.lastY = window.scrollY;
      this.hovered = false;
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
      };
      window.addEventListener('scroll', this.schedule, options);
      window.addEventListener('resize', () => { this.lastY = window.scrollY; this.schedule(); }, options);
      this.bar.addEventListener('pointerenter', () => { this.hovered = true; }, options);
      this.bar.addEventListener('pointerleave', () => { this.hovered = false; }, options);
      this.addEventListener('click', event => {
        const touch = event.pointerType === 'touch' || event.pointerType === 'pen' || matchMedia('(hover: none)').matches;
        if (!touch || event.detail === 0) return;
        const card = event.target.closest('[data-offer-card]');
        if (!card || !this.cards.includes(card) || event.target.closest('.skn-shop-card__action')) return;
        if (event.target.closest('.skn-shop-card__stage') && !card.classList.contains('is-revealed')) event.preventDefault();
        this.revealCard(card);
      }, { capture: true, signal: this.controller.signal });
      document.addEventListener('pointerdown', event => {
        if (!this.cards.some(card => card.contains(event.target))) this.revealCard(null);
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.revealCard(null);
      }, { signal: this.controller.signal });
      this.addEventListener('click', event => {
        const choice = event.target.closest('[data-offer-select], [data-card-select]');
        if (!choice) return;
        const isCard = choice.hasAttribute('data-card-select');
        const index = Number(isCard ? choice.dataset.cardSelect : choice.dataset.offerSelect);
        this.select(index);
        this.distance = 0;
        if (isCard && this.grid) this.grid.scrollTo({ left: this.cards[index].offsetLeft - this.cards[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal: this.controller.signal });
      this.grid?.addEventListener('scroll', () => {
        this.revealCard(null);
        const edge = this.grid.getBoundingClientRect().left;
        const index = this.cards.reduce((best, card, i) => Math.abs(card.getBoundingClientRect().left - edge) < Math.abs(this.cards[best].getBoundingClientRect().left - edge) ? i : best, 0);
        this.highlightCard(index);
        if (!this.locked()) this.select(index);
        this.distance = 0;
      }, options);
      this.cards.forEach((card, index) => card.addEventListener('pointerenter', () => {
        if (matchMedia('(hover: hover)').matches && !this.locked()) { this.select(index); this.distance = 0; }
      }, options));
      this.addEventListener('click', event => {
        if (event.target.closest('[data-home-notify]')) this.dialog.showModal();
        if (event.target.closest('[data-home-close]') || event.target === this.dialog) this.dialog.close();
      }, { signal: this.controller.signal });
      if (this.querySelector('[data-home-signup-result]')) this.dialog.showModal();
      this.update();
      this.setupFeaturedReveal();
    }
    setupFeaturedReveal() {
      if (!this.cards.length || !this.grid || !('IntersectionObserver' in window) || !Element.prototype.animate) return;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      if (motion.matches) return;
      this.revealAnimations = [];
      const prepare = (element, frames, timing) => {
        const animation = element.animate(frames, { ...timing, fill: 'both' });
        animation.pause();
        animation.currentTime = 0;
        animation.onfinish = () => animation.cancel();
        this.revealAnimations.push(animation);
        return animation;
      };
      const heading = this.querySelector('.skn-shop-all__intro h2');
      const line = this.querySelector('.skn-featured__heading-line');
      if (heading && line) {
        const titleAnimation = prepare(line, [
          { transform: 'translateY(105%)' },
          { transform: 'translateY(0)' }
        ], { duration: 1200, easing: 'cubic-bezier(.17,.84,.44,1)' });
        this.headingObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          titleAnimation.play();
          this.headingObserver.disconnect();
        }, { rootMargin: '0px 0px -20% 0px', threshold: 0 });
        this.headingObserver.observe(heading);
      }
      // Sample exponential ease-out to match the reference's 1.8-second card entrance.
      const frames = Array.from({ length: 61 }, (_, step) => {
        const t = step / 60;
        const remaining = step === 60 ? 0 : Math.pow(2, -10 * t);
        return {
          offset: t,
          opacity: 1 - remaining,
          transform: 'translate(' + (120 * remaining) + 'px,' + (50 * remaining) + 'px) rotate(' + (-10 * remaining) + 'deg)'
        };
      });
      const cardAnimations = this.cards.map((card, index) =>
        prepare(card, frames, { duration: 1800, delay: index * 120, easing: 'linear' })
      );
      this.revealObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        cardAnimations.forEach(animation => animation.play());
        this.revealObserver.disconnect();
      }, { threshold: 0 });
      this.revealObserver.observe(this.grid);
      const finishReveal = () => {
        this.headingObserver?.disconnect();
        this.revealObserver?.disconnect();
        this.revealAnimations.forEach(animation => animation.cancel());
      };
      motion.addEventListener('change', event => {
        if (event.matches) finishReveal();
      }, { signal: this.controller.signal });
      this.addEventListener('focusin', finishReveal, { once: true, signal: this.controller.signal });
    }
    locked() { return this.hovered || this.bar.contains(document.activeElement) || this.dialog?.open; }
    revealCard(selected) {
      this.cards.forEach(card => card.classList.toggle('is-revealed', card === selected));
    }
    select(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.offers.length) return;
      this.active = index;
      this.offers.forEach((offer, i) => { offer.hidden = i !== index; });
      this.querySelectorAll('[data-offer-select]').forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    }
    highlightCard(index) {
      this.querySelectorAll('[data-card-select]').forEach((button, i) => {
        if (i === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }
    update() {
      this.frame = 0;
      const y = window.scrollY;
      if (y !== this.lastY) this.revealCard(null);
      const visible = y > 80;
      this.bar.hidden = !visible;
      if (visible && !this.locked()) {
        this.distance += Math.abs(y - this.lastY);
        const step = Math.max(420, window.innerHeight * .65);
        if (this.distance >= step) {
          this.select((this.active + Math.floor(this.distance / step)) % this.offers.length);
          this.distance %= step;
        }
      }
      this.lastY = y;
    }
    disconnectedCallback() {
      this.revealObserver?.disconnect();
      this.headingObserver?.disconnect();
      this.revealAnimations?.forEach(animation => animation.cancel());
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
    }
  });
}


